<template>
  <div class="pm" qid="project-management">

    <!-- Overview has the hero; the other views a compact header -->
    <div class="pm-hero" v-if="view === 'overview'">
      <div class="grow">
        <div class="pm-eyebrow">{{ $t('projects.management.title') }}</div>
        <h1 qid="projects-page-title">{{ $tc('projects.management.hero_title', stats.running, {count: stats.running}) }}</h1>
        <p>{{ heroText }}</p>
        <div class="pm-hero__actions">
          <span class="pm-hero__btn"
                v-if="canCreate"
                @click="startProject"
                qid="pm-start-project">
            <font-awesome-icon icon="plus"/>{{ $t('projects.create_button') }}
          </span>
          <span class="pm-hero__ghost" @click="setView('year')" qid="pm-open-year-plan">
            <font-awesome-icon icon="calendar-alt"/>{{ $t('projects.management.open_year_plan') }}
          </span>
        </div>
      </div>
      <div class="pm-hero__stats">
        <div class="pm-stat"><b>{{ stats.running }}</b><span>{{ $t('projects.management.stats.running') }}</span></div>
        <div class="pm-stat" :class="{'pm-stat--red': stats.overdue}"><b>{{ stats.overdue }}</b><span>{{ $t('projects.management.stats.overdue') }}</span></div>
        <div class="pm-stat"><b>{{ stats.dueThisMonth }}</b><span>{{ $t('projects.management.stats.due_this_month') }}</span></div>
        <div class="pm-stat"><b>{{ stats.recurring }}</b><span>{{ $t('projects.management.stats.recurring') }}</span></div>
      </div>
    </div>

    <div class="pm-head" v-else>
      <div class="grow">
        <div class="pm-eyebrow pm-eyebrow--dark">{{ $t('projects.management.title') }}</div>
        <h1 qid="projects-page-title">{{ $t('projects.management.views.' + view) }}</h1>
      </div>
      <b-button variant="success"
                v-if="canCreate"
                @click="startProject"
                qid="pm-start-project">
        <font-awesome-icon icon="plus" class="mr-2"/>{{ $t('projects.create_button') }}
      </b-button>
    </div>

    <div class="pm-toolbar">
      <div class="pm-seg pm-seg--dark">
        <div :class="{'on': view === 'overview'}" @click="setView('overview')" qid="pm-view-overview">
          <font-awesome-icon icon="th-large"/>{{ $t('projects.management.views.overview') }}
        </div>
        <div :class="{'on': view === 'year'}" @click="setView('year')" qid="pm-view-year">
          <font-awesome-icon icon="calendar-alt"/>{{ $t('projects.management.views.year') }}
        </div>
        <div :class="{'on': view === 'list'}" @click="setView('list')" qid="pm-view-list">
          <font-awesome-icon icon="list-ul"/>{{ $t('projects.management.views.list') }}
        </div>
      </div>
    </div>

    <ProjectsOverview v-if="view === 'overview'"
                      :key="'overview-' + reloadKey"
                      @stats="stats = $event"
                      @run="runProject"
                      @open-view="setView"
    ></ProjectsOverview>
    <YearPlan v-if="view === 'year'" :key="'year-' + reloadKey" @run="runProject"></YearPlan>
    <AllProjects v-if="view === 'list'" :key="'list-' + reloadKey"></AllProjects>

    <!-- The launcher's own button is not shown; the hero and header open it -->
    <span style="display:none;">
      <CreateProjectModal v-if="canCreate" ref="launcher" @reloadContent="reloadKey++"/>
    </span>
  </div>
</template>

<script>
  import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
  import ProjectsOverview from '@/views/Projects/Management/ProjectsOverview.vue';
  import YearPlan from '@/views/Projects/Management/YearPlan.vue';
  import AllProjects from '@/views/Projects/Management/AllProjects.vue';

  const VIEWS = ['overview', 'year', 'list'];

  // One Project management page instead of the Gantt and the three project lists (US 9396)
  export default {
    name: 'ProjectManagement',
    components: {
      CreateProjectModal,
      ProjectsOverview,
      YearPlan,
      AllProjects
    },
    data: function() {
      return {
        stats: {running: 0, overdue: 0, dueThisMonth: 0, recurring: 0, attention: 0, planned: 0},
        reloadKey: 0
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      view() {
        return VIEWS.indexOf(this.$route.query.view) !== -1 ? this.$route.query.view : 'overview';
      },
      canCreate() {
        return !!(this.getClient && this.getClient.projectModule && this.isGranted(this.permissions.PROJECT_CREATE));
      },
      heroText() {
        return this.$tc('projects.management.hero_attention', this.stats.attention, {count: this.stats.attention}) + ' ' +
          this.$tc('projects.management.hero_planned', this.stats.planned, {count: this.stats.planned});
      }
    },
    watch: {
      getClient: function(newValue, oldValue) {
        if (newValue && oldValue && newValue.slug !== oldValue.slug) {
          this.reloadKey++;
        }
      }
    },
    methods: {
      setView(view) {
        if (view !== this.view) {
          this.$router.push({path: '/projects', query: {view: view}});
        }
      },
      startProject() {
        this.$refs.launcher.showCreateProjectModal();
      },
      runProject(project) {
        if (this.$refs.launcher) {
          this.$refs.launcher.showRunModal(project);
        }
      }
    }
  }
</script>
